import { useTellescopeInfiniteQuery, useTellescopeMutation, useUploadTellescopeFile } from "@tellescope/tanstack-query";
import {
  Button,
  Input,
  Label,
  MultiSelect,
  type MultiSelectOption,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@tellescope/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface ShareContentFormProps {
  contactId: string;
  onSubmit?: () => void;
  onCancel?: () => void;
}

type ContentType = "Article" | "PDF" | "Video";
type AssignmentType = "All" | "By Tags" | "Manual" | "Individual";

export const ShareContentForm = ({ contactId, onSubmit, onCancel }: ShareContentFormProps) => {
  const { t } = useTranslation("content");
  const [shareMode, setShareMode] = useState<"existing" | "upload">("existing");
  const [selectedContentId, setSelectedContentId] = useState<string>("");

  // Upload form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contentType, setContentType] = useState<ContentType>("PDF");
  const [category, setCategory] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagOptions, setTagOptions] = useState<MultiSelectOption[]>([]);
  const [assignmentType, setAssignmentType] = useState<AssignmentType>("Individual");
  const [portalPinIndex, setPortalPinIndex] = useState<number>(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Query existing assignments to filter them out
  const assignmentsQuery = useTellescopeInfiniteQuery({
    model: "managed_content_record_assignments",
    method: "getSome",
    request: {
      filter: {
        enduserId: contactId,
      },
    },
    select: (data) => data.pages.flat(),
  });

  // Query all managed content records
  const allContentQuery = useTellescopeInfiniteQuery({
    model: "managed_content_records",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat(),
  });

  // Filter out already-assigned content
  const assignedContentIds = assignmentsQuery.data?.map((a) => a.contentId) ?? [];
  const unassignedContent = allContentQuery.data?.filter((content) => !assignedContentIds.includes(content.id)) ?? [];

  // Mutation to create content assignment
  const assignContentMutation = useTellescopeMutation({
    model: "managed_content_record_assignments",
    method: "createOne",

    onSuccess: () => {
      onSubmit?.();
    },
  });

  // Mutation to create managed content record
  const createContentMutation = useTellescopeMutation({
    model: "managed_content_records",
    method: "createOne",
  });

  // File upload hook
  const { uploadFileAsync, isLoading: isUploading, stage } = useUploadTellescopeFile();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  };

  const handleSubmit = async () => {
    if (shareMode === "existing" && selectedContentId) {
      // Assign existing content
      assignContentMutation.mutate({
        contentId: selectedContentId,
        enduserId: contactId,
      });
    } else if (shareMode === "upload" && selectedFile && title) {
      try {
        // Step 1: Upload the file to get the file record
        const fileRecord = await uploadFileAsync(
          {
            name: selectedFile.name,
            size: selectedFile.size,
            type: selectedFile.type,
          },
          selectedFile
        );

        // Step 2: Create the managed content record with the file attachment
        const contentRecord = await createContentMutation.mutateAsync({
          title,
          description,
          type: contentType,
          category,
          tags: selectedTags,
          assignmentType,
          portalIndex: portalPinIndex,
          textContent: "", // Required field - empty for file-based content
          htmlContent: "", // Required field - empty for file-based content
          // For "Individual" assignment type, set enduserId on the content record itself
          ...(assignmentType === "Individual" && { enduserId: contactId }),
          attachments: [
            {
              secureName: fileRecord.secureName,
              name: fileRecord.name,
              type: fileRecord.type ?? "",
            },
          ],
        });

        // Step 3: For "Manual" assignment type, create an explicit assignment record
        // This is the ONLY type that requires a ManagedContentRecordAssignment
        if (assignmentType === "Manual") {
          await assignContentMutation.mutateAsync({
            contentId: contentRecord.id,
            enduserId: contactId,
          });
        }

        // Success - navigate back
        onSubmit?.();
      } catch (error) {
        console.error("Failed to create content:", error);
        // Error handling is done by the mutation hooks
      }
    }
  };

  // Get file input text and accept types based on content type
  const getFileInputProps = () => {
    switch (contentType) {
      case "PDF":
        return { label: t("Choose PDF"), accept: ".pdf" };
      case "Video":
        return { label: t("Choose a Video"), accept: "video/*" };
      case "Article":
      default:
        return { label: t("Choose File"), accept: "" };
    }
  };

  const fileInputProps = getFileInputProps();

  const isSubmitDisabled =
    (shareMode === "existing" && !selectedContentId) ||
    (shareMode === "upload" && (!selectedFile || !title)) ||
    assignContentMutation.isPending ||
    isUploading ||
    createContentMutation.isPending;

  // Get button text based on current stage
  const getButtonText = () => {
    if (stage === "preparing") return t("Preparing upload...");
    if (stage === "uploading") return t("Uploading file...");
    if (stage === "confirming") return t("Confirming upload...");
    if (createContentMutation.isPending) return t("Creating content...");
    if (assignContentMutation.isPending) return t("Sharing...");
    return t("Share Content");
  };

  return (
    <div className="flex-1 overflow-y-auto space-y-6 pr-2">
      {/* Mode Selection */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t("Share Method")}</Label>
        <div className="flex gap-2">
          <Button
            variant={shareMode === "existing" ? "default" : "outline"}
            onClick={() => setShareMode("existing")}
            className="flex-1"
          >
            {t("Select Existing Content")}
          </Button>
          <Button
            variant={shareMode === "upload" ? "default" : "outline"}
            onClick={() => setShareMode("upload")}
            className="flex-1"
          >
            {t("Upload New Content")}
          </Button>
        </div>
      </div>

      {/* Existing Content Selection */}
      {shareMode === "existing" && (
        <div className="space-y-2">
          <Label htmlFor="content-select" className="text-sm font-medium">
            {t("Select Content")}
          </Label>
          <Select value={selectedContentId} onValueChange={setSelectedContentId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("Choose content to share")} />
            </SelectTrigger>
            <SelectContent>
              {unassignedContent.length === 0 ? (
                <div className="p-2 text-sm text-muted-foreground">{t("No available content")}</div>
              ) : (
                unassignedContent.map((content) => (
                  <SelectItem key={content.id} value={content.id}>
                    {content.title} {content.category ? `(${content.category})` : ""}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Upload New Content */}
      {shareMode === "upload" && (
        <>
          {/* Title and Description */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-medium">
                {t("Title")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("Enter title")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium">
                {t("Description")}
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("Enter description")}
                rows={3}
              />
            </div>
          </div>

          {/* Type and Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="content-type" className="text-sm font-medium">
                {t("Type")}
              </Label>
              <Select value={contentType} onValueChange={(value) => setContentType(value as ContentType)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Article" disabled>
                    {t("Article")} ({t("Coming Soon")})
                  </SelectItem>
                  <SelectItem value="PDF">{t("PDF")}</SelectItem>
                  <SelectItem value="Video">{t("Video")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category" className="text-sm font-medium">
                {t("Category")}
              </Label>
              <Input
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder={t("Enter category")}
              />
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags" className="text-sm font-medium">
              {t("Tags")}
            </Label>
            <MultiSelect
              id="tags"
              options={tagOptions}
              value={selectedTags}
              onValueChange={setSelectedTags}
              placeholder={t("Add tags")}
              creator={{
                render: (inputValue) => (
                  <span>
                    {t("Create")}: <strong>{inputValue}</strong>
                  </span>
                ),
                transform: (inputValue) => inputValue,
                onCreate: (newOption) => {
                  setTagOptions([...tagOptions, newOption]);
                },
              }}
            />
          </div>

          {/* Assignment Type and Portal Pin Index */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="assignment-type" className="text-sm font-medium">
                {t("Assignment")}
              </Label>
              <Select value={assignmentType} onValueChange={(value) => setAssignmentType(value as AssignmentType)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">{t("All")}</SelectItem>
                  <SelectItem value="By Tags">{t("By Tags")}</SelectItem>
                  <SelectItem value="Manual">{t("Manual")}</SelectItem>
                  <SelectItem value="Individual">{t("Individual")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="portal-pin-index" className="text-sm font-medium">
                {t("Portal Pin Index")}
              </Label>
              <Input
                id="portal-pin-index"
                type="number"
                min={0}
                value={portalPinIndex}
                onChange={(e) => setPortalPinIndex(parseInt(e.target.value) || 0)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">{t("Pins to top, 0 is highest")}</p>
            </div>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="content-upload" className="text-sm font-medium">
              {fileInputProps.label} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="content-upload"
              type="file"
              onChange={handleFileChange}
              accept={fileInputProps.accept}
              disabled={contentType === "Article"}
            />
            {selectedFile && (
              <p className="text-sm text-muted-foreground">
                {t("Selected")}: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            {t("Cancel")}
          </Button>
        )}
        <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
          {getButtonText()}
        </Button>
      </div>
    </div>
  );
};
